import { useI18n } from "@/i18n/I18nContext";
import { ms, timeOf } from "@/components/health/HealthGauge";

const STATE_TONE = { ok: "bg-emerald-500/20 text-emerald-200", dead: "bg-red-600/30 text-red-100", hung: "bg-amber-500/25 text-amber-100", stopped: "bg-neutral-700 text-neutral-200" };
const label = (t, n) => (n === "robot" ? t("heal.robot") : n.startsWith("wd") ? t("heal.watchdog", { n: n.slice(2) }) : t(`heal.worker.${n}`));

const Row = ({ w }) => {
  const { t } = useI18n();
  return (
    <li className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-white/5 py-2 text-sm last:border-0" data-testid={`health-robot-${w.name}`}>
      <span className="w-full min-w-0 break-words text-neutral-100 sm:w-auto sm:flex-1">{label(t, w.name)}</span>
      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATE_TONE[w.state]}`} data-testid={`health-robot-state-${w.name}`}>{t(`heal.state.${w.state}`)}</span>
      {w.cycle && <span className="text-xs text-neutral-400"><bdi>{t("heal.cycle")} {w.cycle}</bdi></span>}
      <span className="text-xs text-neutral-400"><bdi>{t("heal.beat")} {w.beat_age_s.toFixed(1)} s</bdi></span>
      <span className="text-xs text-neutral-400" data-testid={`health-robot-restarts-${w.name}`}>{t("heal.restarts")} {w.restarts}</span>
    </li>
  );
};

export const RobotsCard = ({ robots, workers }) => {
  const { t } = useI18n();
  return (
    <section className="card-carbon min-w-0 rounded-2xl p-5" data-testid="health-robots">
      <h2 className="font-display text-base font-bold text-white md:text-lg">{t("heal.robots")}</h2>
      <ul className="mt-2">{robots.map((w) => <Row key={w.name} w={w} />)}</ul>
      <h3 className="mt-4 text-xs font-semibold uppercase tracking-wider text-neutral-400">{t("heal.workers")}</h3>
      <ul className="mt-1">{workers.map((w) => <Row key={w.name} w={w} />)}</ul>
    </section>
  );
};

const KIND_TONE = { repair: "text-emerald-300", repair_failed: "text-red-300", robot_restart: "text-sky-300", ui_error: "text-amber-300", alert: "text-red-300" };

export const JournalCard = ({ entries }) => {
  const { t } = useI18n();
  return (
    <section className="card-carbon min-w-0 rounded-2xl p-5" data-testid="health-journal">
      <h2 className="font-display text-base font-bold text-white md:text-lg">{t("heal.journal")} <span className="text-xs text-neutral-500" data-testid="health-journal-count">({entries.length})</span></h2>
      {!entries.length && <p className="mt-2 text-sm text-neutral-400">{t("heal.journalEmpty")}</p>}
      <ol className="mt-2 max-h-[28rem] space-y-2 overflow-y-auto pe-1">
        {entries.map((e) => (
          <li key={e.id} className="min-w-0 rounded-lg bg-black/30 p-2 text-xs" data-testid={`health-journal-${e.id}`}>
            <p className="flex flex-wrap gap-x-2"><span className="text-neutral-500">{timeOf(e.at)}</span>
              <span className={`font-semibold ${KIND_TONE[e.kind] || "text-neutral-200"}`}>{t(`heal.kind.${e.kind}`)}</span>
              {e.subsystems?.length > 0 && <span className="text-neutral-300">{e.subsystems.map((s) => (s === "robots" ? t("heal.robots") : t(`heal.sub.${s}`))).join(", ")}</span>}
              {e.recover_ms != null && <span className="text-neutral-400"><bdi>{ms(e.recover_ms)}</bdi></span>}</p>
            {e.gauge_before != null && <p className="mt-0.5 text-neutral-400"><bdi>{e.gauge_before.toFixed(4)} % → {e.gauge_after.toFixed(4)} %</bdi></p>}
            {e.repairs?.length > 0 && <p className="mt-0.5 break-words text-neutral-300"><bdi>{e.repairs.map((r) => `${r.name.split(":").pop()} ${r.ok ? "✓" : "✗"}`).join(" → ")}</bdi></p>}
            {e.message && <p className="mt-0.5 break-words text-neutral-400"><bdi>{e.message}</bdi></p>}
          </li>
        ))}
      </ol>
    </section>
  );
};
